import { useState } from "react";
import { api } from "../api.js";
import { useApp } from "../app.jsx";
import { navigate } from "../router.jsx";
import { money, fmtDateShort } from "../format.js";
import {
  Async, BarList, Button, Card, Empty, ErrorPanel, Grid, KPI, Modal, Sep, Table, TopBar, Wrap,
  useAsync, useToast,
} from "../ui.jsx";

export default function Payments() {
  const { settings, me } = useApp();
  const toast = useToast();
  const state = useAsync(() => api.get("payments"), []);
  const [removing, setRemoving] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const sym = settings.currency_symbol;
  const canDelete = !!me.permissions["payments.manage"];

  async function remove() {
    setError(null);
    setBusy(true);
    try {
      await api.del(`payments/${removing.id}`);
      toast.push("Payment removed");
      setRemoving(null);
      state.reload();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <TopBar crumb="Get paid" title="Payments" />

      <Wrap>
        {error && <ErrorPanel error={error} />}
        <Async state={state}>
          {(d) => {
            const list = d.list || [];
            const totals = d.totals || {};
            const byMethod = d.byMethod || [];

            return (
              <>
                <Grid cols={4}>
                  <KPI label="Collected this month" value={money(totals.this_month, sym)} sub="cash received" />
                  <KPI label="Collected YTD" value={money(totals.ytd, sym)} sub="this calendar year" />
                  <KPI
                    label="Payments recorded"
                    value={Number(totals.count || 0)}
                    sub={`${list.length} shown below`}
                  />
                  <KPI
                    label="Online payments"
                    value={<span className="muted" style={{ fontSize: 19 }}>Not connected</span>}
                    sub="every payment here was entered by hand"
                  />
                </Grid>

                <Grid cols="1.6fr 1fr" className="mt" style={{ alignItems: "start" }}>
                  <Card bare title="All payments">
                    <Table
                      rows={list}
                      onRowClick={(p) => navigate(`/invoices/${p.invoice_id}`)}
                      empty={<Empty icon="◈">No payments recorded yet</Empty>}
                      columns={[
                        {
                          key: "paid_on",
                          label: "Date",
                          render: (p) => <span className="sm dim">{fmtDateShort(p.paid_on)}</span>,
                        },
                        { key: "client_name", label: "Client" },
                        { key: "number", label: "Invoice", render: (p) => <b className="num">{p.number}</b> },
                        { key: "method", label: "Method", render: (p) => <span className="sm dim">{p.method}</span> },
                        {
                          key: "reference",
                          label: "Reference",
                          render: (p) => <span className="sm muted num">{p.reference || "—"}</span>,
                        },
                        {
                          key: "amount",
                          label: "Amount",
                          align: "right",
                          render: (p) => <b className="num">{money(p.amount, sym)}</b>,
                        },
                        canDelete && {
                          key: "remove",
                          label: "",
                          align: "right",
                          render: (p) => (
                            <span
                              className="x"
                              title="Remove this payment"
                              style={{ fontSize: 16 }}
                              onClick={(ev) => {
                                ev.stopPropagation();
                                setRemoving(p);
                              }}
                            >
                              ×
                            </span>
                          ),
                        },
                      ]}
                    />
                  </Card>

                  <Card title="By method">
                    {byMethod.length ? (
                      <BarList
                        rows={byMethod.map((m) => ({ label: m.method, value: m.amount }))}
                        format={(v) => money(v, sym)}
                      />
                    ) : (
                      <Empty icon="◈">Nothing collected yet</Empty>
                    )}
                    <Sep />
                    <div className="tiny muted">
                      Payments are recorded against an invoice — open the invoice to add one. Click any row to jump
                      straight to it.
                    </div>
                  </Card>
                </Grid>
              </>
            );
          }}
        </Async>
      </Wrap>

      {removing && (
        <Modal
          title="Remove this payment?"
          width={460}
          onClose={() => setRemoving(null)}
          footer={
            <>
              <Button onClick={() => setRemoving(null)}>Cancel</Button>
              <Button kind="dgr" busy={busy} onClick={remove}>
                Remove payment
              </Button>
            </>
          }
        >
          {error && <ErrorPanel error={error} />}
          <div className="sm">
            {money(removing.amount, sym)} received {fmtDateShort(removing.paid_on)} by {removing.method} against{" "}
            <b className="num">{removing.number}</b>.
          </div>
          <div className="tiny muted" style={{ marginTop: 8 }}>
            The invoice balance goes back up straight away, and this cannot be undone.
          </div>
        </Modal>
      )}
    </>
  );
}
